import axios from "axios"

import api from "@/lib/axios"

/**
 * Seat registration for the free in-person mock test.
 *
 * The POST is deliberately unauthenticated — it backs a form on a public page —
 * so it goes through the shared client without any token requirement.
 */

export interface FreeMockRegistrationPayload {
    full_name: string
    email: string
    phone: string
    institution: string
    previous_sat_score?: number | null
    expected_sat_score?: number | null
    /** Cloudflare Turnstile token, verified server-side before anything is written. */
    turnstile_token?: string | null
    coupon_code?: string | null
}

export interface FreeMockRegistrationResult {
    status: "registered" | "already_registered" | "payment_required"
    full_name: string
    message: string
    access_key?: string | null
    payment_status?: string | null
    bkash_url?: string | null
    payment_id?: string | null
    amount_paid?: number | null
    final_price?: number | null
}

export interface CouponValidationResult {
    valid: boolean
    code?: string
    name?: string
    discount_type?: string
    discount_value?: number
    original_price?: number
    discount_amount?: number
    final_price?: number
    coupon_id?: number | null
    message: string
}

/** A field-level error the server rejected, mapped back to the form. */
export interface FieldErrors {
    [field: string]: string
}

export class RegistrationError extends Error {
    fieldErrors: FieldErrors
    retryAfterMessage?: string

    constructor(message: string, fieldErrors: FieldErrors = {}, retryAfterMessage?: string) {
        super(message)
        this.name = "RegistrationError"
        this.fieldErrors = fieldErrors
        this.retryAfterMessage = retryAfterMessage
    }
}

export interface RegistrationConfig {
    /** Public Turnstile site key, served from the backend .env so it is not
     *  duplicated into a frontend env file. Null when unconfigured. */
    turnstile_site_key: string | null
}

export interface InhouseTestSummary {
    id: number
    name: string
    description?: string | null
    total_questions: number
}

export interface FreeMockEntryResult {
    full_name: string
    /** Masked server-side — a guessed key must not surrender a usable address. */
    email_masked: string
    phone_masked: string
    institution: string
    previous_sat_score?: number | null
    expected_sat_score?: number | null
    access_key: string
    event_date: string
    event_time: string
    venue_name?: string
    location_text: string
    map_link?: string | null
    tests: InhouseTestSummary[]
}

export const freeMockRegistration = {
    /** Exchange an approved access key for the student's own details. */
    enter: async (accessKey: string): Promise<FreeMockEntryResult> => {
        try {
            const res = await api.post<FreeMockEntryResult>("/free-mock/enter", {
                access_key: accessKey,
            })
            return res.data
        } catch (err) {
            if (!axios.isAxiosError(err) || !err.response) {
                throw new RegistrationError(
                    "We couldn't reach the server. Check your connection and try again."
                )
            }
            const detail = err.response.data?.detail
            throw new RegistrationError(
                typeof detail === "string"
                    ? detail
                    : Array.isArray(detail) && detail[0]?.msg
                      ? String(detail[0].msg).replace(/^Value error,\s*/, "")
                      : "That key didn't work. Please try again."
            )
        }
    },

    getConfig: async (): Promise<RegistrationConfig> => {
        const res = await api.get<RegistrationConfig>("/free-mock/config")
        return res.data
    },

    register: async (
        payload: FreeMockRegistrationPayload
    ): Promise<FreeMockRegistrationResult> => {
        try {
            const res = await api.post<FreeMockRegistrationResult>(
                "/free-mock/register",
                payload
            )
            return res.data
        } catch (err) {
            if (!axios.isAxiosError(err) || !err.response) {
                throw new RegistrationError(
                    "We couldn't reach the server. Check your connection and try again."
                )
            }

            const { status, data } = err.response

            // 429 carries a human-readable wait message from the rate limiter.
            if (status === 429) {
                throw new RegistrationError(
                    typeof data?.detail === "string"
                        ? data.detail
                        : "Too many attempts. Please try again shortly."
                )
            }

            // The captcha check answers 400 with a sentence meant for the reader.
            if (status === 400 && typeof data?.detail === "string") {
                throw new RegistrationError(data.detail)
            }

            // FastAPI 422: turn Pydantic's positional errors into per-field
            // messages so each one lands under the input that caused it,
            // rather than as one lump at the top of the form.
            if (status === 422 && Array.isArray(data?.detail)) {
                const fieldErrors: FieldErrors = {}
                for (const item of data.detail) {
                    const field = Array.isArray(item?.loc)
                        ? item.loc[item.loc.length - 1]
                        : undefined
                    if (typeof field === "string") {
                        fieldErrors[field] = String(item?.msg ?? "Invalid value").replace(
                            /^Value error,\s*/,
                            ""
                        )
                    }
                }
                throw new RegistrationError(
                    "Please check the highlighted fields.",
                    fieldErrors
                )
            }

            throw new RegistrationError(
                typeof data?.detail === "string"
                    ? data.detail
                    : "Something went wrong. Please try again."
            )
        }
    },

    validateCoupon: async (
        code: string,
        previousScore?: number | null
    ): Promise<CouponValidationResult> => {
        try {
            const res = await api.post<CouponValidationResult>("/free-mock/validate-coupon", {
                code,
                previous_sat_score: previousScore,
            })
            return res.data
        } catch (err: any) {
            return {
                valid: false,
                message: err?.response?.data?.detail || "Could not validate coupon.",
            }
        }
    },

    executeBkash: async (
        paymentId: string,
        status: string
    ): Promise<{
        success: boolean
        message: string
        registration_id?: number
        access_key?: string
        full_name?: string
        trx_id?: string
        amount_paid?: number
    }> => {
        const res = await api.post("/free-mock/bkash/execute", {
            payment_id: paymentId,
            status,
        })
        return res.data
    },
}
